/**
 * useErrorHandler Composable
 * Centralized error handling with ticket creation support
 */
import { ref } from 'vue';
import { useRouter } from 'vue-router';

export function useErrorHandler() {
  const router = useRouter();
  const errorContext = ref(null);

  /**
   * Store error context for ticket creation
   */
  const setErrorContext = (error, context = {}) => {
    errorContext.value = {
      message: error?.response?.data?.message || 
               error?.response?.data?.err || 
               error?.message || 
               'Une erreur s\'est produite',
      status: error?.response?.status,
      statusText: error?.response?.statusText,
      url: error?.config?.url,
      method: error?.config?.method,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      currentPath: window.location.pathname,
      ...context
    };
    
    // Store in sessionStorage for persistence across navigation
    sessionStorage.setItem('lastError', JSON.stringify(errorContext.value));
  };

  /**
   * Get stored error context
   */
  const getErrorContext = () => {
    if (errorContext.value) {
      return errorContext.value;
    }
    
    const stored = sessionStorage.getItem('lastError');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  /**
   * Navigate to support page with error context
   */
  const navigateToSupport = (error = null) => {
    if (error) {
      setErrorContext(error);
    }
    
    // Navigate to support page
    router.push({
      path: '/support',
      query: {
        error: 'true',
        ...(errorContext.value ? { context: 'stored' } : {})
      }
    });
  };

  /**
   * Create ticket directly from error context
   * @param {string} userEmail - User email
   * @param {string} service - Service name (optional, default: from VUE_APP_TICKET_SERVICE_NAME)
   * @returns {Promise<string>} Ticket ID
   */
  const createTicketFromError = async (userEmail, service = null) => {
    const context = getErrorContext();
    if (!context) {
      throw new Error('No error context available');
    }

    // Use service from env if not provided
    const serviceName = service || process.env.VUE_APP_TICKET_SERVICE_NAME || 'AgriEdge Master';

    // Use dynamic import to avoid circular dependencies
    const ticketServiceModule = await import('@/services/ticketService');
    return await ticketServiceModule.ticketService.createTicketFromError(context, userEmail, serviceName);
  };

  /**
   * Check if error should show ticket button
   * Show for server errors (500, 502, 503, 504) and network errors
   */
  const shouldShowTicketButton = (error) => {
    if (!error) return false;
    
    // Server errors
    const status = error?.response?.status;
    if (status >= 500 && status < 600) {
      return true;
    }
    
    // Network errors
    if (!error.response && error.message) {
      const networkErrors = ['network', 'timeout', 'connection', 'failed'];
      return networkErrors.some(keyword => 
        error.message.toLowerCase().includes(keyword)
      );
    }
    
    return false;
  };

  /**
   * Clear error context
   */
  const clearErrorContext = () => {
    errorContext.value = null;
    sessionStorage.removeItem('lastError');
  };

  return {
    errorContext,
    setErrorContext,
    getErrorContext,
    navigateToSupport,
    createTicketFromError,
    shouldShowTicketButton,
    clearErrorContext
  };
}

